import { defineStore } from 'pinia'
import type { AppUser, UserRole } from '@/types'
import { t } from '@/i18n'

function translateError(code: string): string {
  const key = `errors.${code}`
  return t(key) !== key ? t(key) : t('errors.unknown')
}

export type { UserRole, AppUser }

function userFromResponse(data: { username: string; role: string }): AppUser {
  const role: UserRole = ['admin', 'editor', 'writer'].includes(data.role) ? (data.role as UserRole) : 'writer'
  return { id: data.username, email: data.username, displayName: data.username, photoURL: null, role }
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null as AppUser | null,
    isLoading: true,
    error: null as string | null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.user,
    isAdmin: (state) => state.user?.role === 'admin',
    isEditor: (state) => state.user?.role === 'editor' || state.user?.role === 'admin',
    isWriter: (state) => !!state.user,
  },

  actions: {
    async init(): Promise<void> {
      try {
        const res = await fetch('/api/me', { credentials: 'same-origin' })
        if (res.ok) {
          const data = await res.json()
          this.user = userFromResponse(data)
        } else {
          this.user = null
        }
      } catch {
        this.user = null
      }
      this.isLoading = false
    },

    async signIn(username: string, password: string) {
      this.error = null
      if (!username.trim() || !password.trim()) {
        this.error = t('errors.EMPTY_CREDENTIALS')
        return false
      }
      try {
        const res = await fetch('/api/login', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password }),
        })
        if (res.ok) {
          const data = await res.json()
          this.user = userFromResponse(data)
          return true
        }
        const data = await res.json().catch(() => ({}))
        this.error = translateError(data.code)
        return false
      } catch {
        this.error = t('errors.network')
        return false
      }
    },

    async signUp(email: string, password: string, role: UserRole, displayName?: string) {
      this.error = null
      if (!email.trim() || !password.trim()) {
        this.error = t('errors.EMPTY_CREDENTIALS')
        return false
      }
      // TODO: wire to registration endpoint when available
      this.user = { id: '1', email, displayName: displayName ?? email.split('@')[0], photoURL: null, role }
      return true
    },

    async logout() {
      await fetch('/logout', { method: 'POST', credentials: 'same-origin' }).catch(() => {})
      this.user = null
      this.error = null
    },
  },
})
